/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import {
    FlexRow,
    FlexCol,
    Icon,
    Button,
    Heading,
    withLDConsumer,
    DividerNew as Divider,
} from 'sarsaparilla';
import { Footer } from 'site-kit';
import SearchFilterTourTypes from 'ui-search/src/components/SearchFilterTourType';
import { SearchFilterTourDuration } from 'ui-search/src/components/SearchFilterTourDuration';

import InventoryTypeFilters from 'ui-search/src/components/filters/inventoryTypes';
import CampingFilters from 'ui-search/src/components/filters/camping/CampingFilters';
import AdditionalPreferences from 'ui-search/src/components/filters/camping/AdditionalPreferences';
import * as tripPlannerActions from '../actions/tripPlanner';
import { getTripStartEndDates } from '../utils/tripUtils';

const INVENTORY_TYPES = {
    PERMITS: 'permits',
    POI: 'poi',
    CAMPING: 'camping',
    DAY_USE: 'dayuse',
    VENUES: 'venues',
    ACTIVITY_PASS: 'activitypass',
    TREE_PERMITS: 'treepermits',
    REC_AREA: 'recarea',
    TICKETS: 'tickets',
};

const propTypes = {
    dispatch: PropTypes.func,
    onApplyFilters: PropTypes.func,
    tripPlanner: PropTypes.object,
    tripPlannerAggregations: PropTypes.object,
    tripPlannerSearchResults: PropTypes.object,
};

const defaultProps = {};

class TripPlannerFiltersContainer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            radius: props.tripPlanner.trip.radius,
            uiFilters: props.tripPlanner.uiFilters,
        };

        this.onApplyFilter = this.onApplyFilter.bind(this);
        this.onFilterToggle = this.onFilterToggle.bind(this);
        this.onFilterChange = this.onFilterChange.bind(this);
        this.onFilterRemove = this.onFilterRemove.bind(this);
        this.onApplyFilters = this.onApplyFilters.bind(this);
        this.onResetFilters = this.onResetFilters.bind(this);
        this.onHideFilters = this.onHideFilters.bind(this);
    }

    componentDidMount() {
        this.props.dispatch(tripPlannerActions.fetchSearchFiltersAggregations());
    }

    onApplyFilter() {}

    onCriteriaUpdate = (key, item) => {
        this.props.dispatch(tripPlannerActions.updateTripPlannerCriteria(key, item));
    };

    onFilterToggle(filterName, item) {
        this.props.dispatch(tripPlannerActions.toggleSearchFilter(filterName, item));
    }

    onFilterChange(filterName, item) {
        this.props.dispatch(tripPlannerActions.setSearchFilter(filterName, item));
    }

    onFilterRemove(filterName, item) {
        this.props.dispatch(tripPlannerActions.removeSearchFilter(filterName, item));
    }

    onResetFilters() {
        this.props.dispatch(tripPlannerActions.resetSearchFilters());
    }

    onApplyFilters() {
        this.props.dispatch(tripPlannerActions.setFiltersVisible(false));
    }

    onHideFilters() {
        this.props.dispatch(tripPlannerActions.setFiltersVisible(false));
    }

    getCampsiteFilterCount = () => {
        let campsiteFilterCount = 0;

        if (this.props.tripPlanner && this.props.tripPlanner.uiFilters) {
            campsiteFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.accommodations
            ).length;
            campsiteFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.campsiteUseTypes
            ).length;
            campsiteFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.campsiteTypes
            ).length;
            campsiteFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.campsiteAmenities
            ).length;
            campsiteFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.campsiteEquipment
            ).length;
            campsiteFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.campsiteElectricityHookup
            ).length;
            campsiteFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.campsiteVehicleLength
            ).length;
            campsiteFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.occupants
            ).length;
        }
        return campsiteFilterCount;
    };

    getTourFilterCount = () => {
        let tourFilterCount = 0;
        if (this.props.tripPlanner && this.props.tripPlanner.uiFilters) {
            tourFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.tourTypes
            ).length;
            tourFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.tourAccessible
            ).length;
            tourFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.tourDifficulty
            ).length;
            tourFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.tourDuration
            ).length;
            tourFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.tourPrice
            ).length;
            tourFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.tourTime
            ).length;
        }
        return tourFilterCount;
    };

    getActivitiesFilterCount = () => {
        let activitiesFilterCount = 0;

        if (this.props.tripPlanner && this.props.tripPlanner.uiFilters) {
            activitiesFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.activities
            ).length;
        }
        return activitiesFilterCount;
    };

    getAgencyFilterCount = () => {
        let agencyFilterCount = 0;

        if (this.props.tripPlanner && this.props.tripPlanner.uiFilters) {
            agencyFilterCount += Object.keys(
                this.props.tripPlanner.uiFilters.agencies
            ).length;
        }
        return agencyFilterCount;
    };

    getTotalFilterCount = () => {
        let filtersCount =
            this.getCampsiteFilterCount(this.campsiteFilterCount) +
            this.getActivitiesFilterCount(this.activitiesFilterCount) +
            this.getTourFilterCount(this.tourFilterCount) +
            this.getAgencyFilterCount(this.agencyFilterCount);
        const fg = get(this.props, 'tripPlanner.fg', []);
        if (fg.length > 0) {
            filtersCount += fg.length;
        }
        return filtersCount;
    };

    refresh() {
        setTimeout(() => {
            this.props.dispatch(tripPlannerActions.fetchSearchFiltersAggregations());
            this.props.dispatch(
                tripPlannerActions.fetchTripPlannerListSearchResults(
                    this.props.destination,
                    this.props.destinationIndex
                )
            );
        }, 300);
    }

    checkInventoryType = (value) => {
        const { tripPlanner } = this.props;
        return Boolean(tripPlanner.fg && tripPlanner.fg?.includes(value));
    };

    getInventoryStatus = () => {
        const inventoryStatus = Object.values(INVENTORY_TYPES).reduce(
            (acc, type) => ({
                ...acc,
                [type]: this.checkInventoryType(type),
            }),
            {}
        );

        return {
            ...inventoryStatus,
            hasInventory: Object.values(inventoryStatus).some(Boolean),
        };
    };

    renderSearchFilters = (tourFilterCount) => {
        const {
            camping: isCampingSearch,
            dayuse: isDayUseSearch,
            tickets: isTicketsSearch,
            hasInventory,
        } = this.getInventoryStatus();

        // Same helper the search requests use to populate start_date/end_date, so
        // the date-gated filters agree with what actually gets sent.
        const [tripStartDate, tripEndDate] = getTripStartEndDates(
            this.props.tripPlanner.trip
        );

        return (
            <FlexRow className="search-filters-dropdown-body">
                <FlexCol xs={12}>
                    <section className="search-filters-filter-container">
                        <InventoryTypeFilters
                            aggregations={this.props.tripPlannerAggregations}
                            onApplyFilters={this.onApplyFilter}
                            storePath="tripPlanner"
                            onCriteriaUpdate={this.onCriteriaUpdate}
                        />
                    </section>
                    {(isCampingSearch || isDayUseSearch || !hasInventory) && (
                        <section>
                            <CampingFilters
                                aggregations={this.props.tripPlannerAggregations}
                                onApplyFilters={this.onApplyFilter}
                                search={this.props.tripPlanner}
                                storePath="tripPlanner"
                                onCriteriaUpdate={this.onCriteriaUpdate}
                                hasDates={Boolean(tripStartDate && tripEndDate)}
                            />
                        </section>
                    )}
                </FlexCol>

                {(isTicketsSearch || !hasInventory) && (
                    <FlexCol xs={12} className="search-filters-type-wrap">
                        <div className="search-filters-section-header rounded-layout mb-1 mt-3">
                            <Heading headingLevel={2} appearance="h5">
                                Tickets & Tours
                            </Heading>
                            <span>
                                {this.getTourFilterCount(tourFilterCount)}{' '}
                                {this.getTourFilterCount(tourFilterCount) === 1 &&
                                    'Filter'}{' '}
                                {this.getTourFilterCount(tourFilterCount) !== 1 &&
                                    'Filters'}{' '}
                                Selected
                            </span>
                        </div>

                        <div className="search-filters-filter-container pb-2">
                            <SearchFilterTourTypes
                                uiFilters={this.props.tripPlanner.uiFilters}
                                aggregations={this.props.tripPlannerAggregations}
                                onFilterToggle={this.onFilterToggle}
                                onApplyFilters={this.onApplyFilter}
                            />

                            <div className="pb-4 pt-0">
                                <Divider />
                            </div>

                            <div className="pb-3">
                                <SearchFilterTourDuration
                                    onFilterChange={this.onFilterChange}
                                    onFilterRemove={this.onFilterRemove}
                                    onApplyFilters={this.onApplyFilter}
                                    isDisabled={isEmpty(
                                        this.props.tripPlannerAggregations?.tourTypes
                                    )}
                                />
                            </div>
                        </div>
                    </FlexCol>
                )}

                <FlexCol xs={12}>
                    <AdditionalPreferences
                        uiFilters={this.props.tripPlanner.uiFilters}
                        aggregations={this.props.tripPlannerAggregations}
                        onFilterToggle={this.onFilterToggle}
                        onApplyFilters={this.onApplyFilter}
                    />
                </FlexCol>
            </FlexRow>
        );
    };

    render() {
        const totalFiltersCount = this.getTotalFilterCount(this.filtersCount);
        const tripPlannerFilters = this.renderSearchFilters(totalFiltersCount);
        const totalItems = get(
            this.props.tripPlannerSearchResults,
            'geoResults.length',
            0
        );

        // SHOW THE TOTAL NUMBER OF RESULTS ON THE APPLY BUTTON
        const totalItemLabel = totalItems === 1 ? 'Result' : 'Results';
        const totalItemText =
            totalItems > 0 ? `View ${totalItems} ${totalItemLabel}` : 'No Results';

        const clearBtn = (
            <Button
                appearance="link"
                iconAfterElement={<Icon iconName="loop" />}
                className="reset"
                id="testhook-reset"
                onClick={this.onResetFilters}
            >
                Clear All Filters
            </Button>
        );
        return (
            <div className="search-filters-dropdown-wrap new-layout">
                {tripPlannerFilters}

                <FlexRow hasGutters={false} className="search-filters-actions">
                    <FlexCol className="search-filters-inner-wrap">
                        {totalFiltersCount <= 0 ? '' : clearBtn}

                        <div className="search-close-cancel-wrap">
                            <Button
                                appearance="link"
                                id="testhook-hide"
                                onClick={this.onHideFilters}
                            >
                                Cancel
                            </Button>

                            <Button
                                className="close-btn"
                                iconAfterElement={<Icon iconName="arrow-up" />}
                                size="lg"
                                id="testhook-apply"
                                onClick={this.onApplyFilters}
                            >
                                {totalFiltersCount <= 0 ? 'Hide Filters' : totalItemText}
                            </Button>
                        </div>
                    </FlexCol>
                </FlexRow>

                <div className="results-footer-wrap">
                    <Footer inline />
                </div>
            </div>
        );
    }
}

TripPlannerFiltersContainer.propTypes = propTypes;
TripPlannerFiltersContainer.defaultProps = defaultProps;

function select(state) {
    return {
        tripPlanner: state.tripPlanner,
        tripPlannerAggregations: state.tripPlannerAggregations,
        tripPlannerSearchResults: state.tripPlannerSearchResults,
    };
}

export default connect(select)(withLDConsumer()(TripPlannerFiltersContainer));
